'use client';

import { Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { useRouter } from 'next/navigation';

interface PricingCardProps {
  title: string;
  price: {
    monthly: number;
    yearly: number;
  };
  description?: string;
  features: string[];
  isPopular?: boolean;
  isYearly: boolean;
}

// Counter animation component with improved animation
const Counter = ({
  from,
  to,
  duration = 0.5,
}: {
  from: number;
  to: number;
  duration?: number;
}) => {
  const [count, setCount] = useState(from);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    setIsAnimating(true);
    let startTime: number;
    let animationFrame: number;

    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / (duration * 1000), 1);

      // Using cubic-bezier timing function similar to the provided CSS
      const easeProgress = cubicBezier(0, 0.7, 0.5, 1, progress);

      setCount(from + easeProgress * (to - from));

      if (progress < 1) {
        animationFrame = requestAnimationFrame(step);
      } else {
        setIsAnimating(false);
      }
    };

    animationFrame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(animationFrame);
  }, [from, to, duration]);

  // Cubic bezier implementation
  function cubicBezier(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    t: number,
  ): number {
    const cx = 3 * x1;
    const bx = 3 * (x2 - x1) - cx;
    const ax = 1 - cx - bx;
    const cy = 3 * y1;
    const by = 3 * (y2 - y1) - cy;
    const ay = 1 - cy - by;

    const tSquared = t * t;
    const tCubed = tSquared * t;

    return ay * tCubed + by * tSquared + cy * t;
  }

  // Format the number with 2 decimal places
  const formattedCount = isAnimating ? Math.floor(count) : to.toFixed(0);

  return (
    <span className="font-feature-settings-tnum font-variant-numeric-tabular-nums">
      {formattedCount}
      <span className="text-4xl">.99</span>
    </span>
  );
};

export default function PricingCard({
  title,
  price,
  description,
  features,
  isPopular = false,
  isYearly,
}: PricingCardProps) {
  // Calculate the display price
  const monthlyPrice = price.monthly;
  const yearlyPricePerMonth = Math.floor(price.yearly / 12);
  const yearlyTotal = price.yearly;

  const router = useRouter();

  return (
    <motion.div
      className={`rounded-3xl overflow-hidden relative ${
        isPopular
          ? 'border-[#4d2ef5]'
          : 'bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800'
      } border-2 shadow-xl hover:shadow-2xl transition-all duration-300`}
      whileHover={{
        y: -8,
        boxShadow: '0 25px 50px -12px rgba(77, 46, 245, 0.25)',
      }}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
    >
      {/* Modern gradient background for popular card */}
      {isPopular && (
        <div className="absolute inset-0 bg-gradient-to-br from-[#4d2ef5] via-[#5d3df6] to-[#2a1a80] opacity-95 z-0" />
      )}

      {/* Subtle animated pattern for popular card */}
      {isPopular && (
        <div className="absolute inset-0 z-0 opacity-10">
          <svg className="size-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern
                id="pricing-pattern"
                x="0"
                y="0"
                width="40"
                height="40"
                patternUnits="userSpaceOnUse"
              >
                <path
                  d="M0 20 L20 0 L40 20 L20 40 Z"
                  fill="none"
                  stroke="white"
                  strokeWidth="1"
                />
              </pattern>
            </defs>
            <rect
              x="0"
              y="0"
              width="100%"
              height="100%"
              fill="url(#pricing-pattern)"
            />
          </svg>
        </div>
      )}

      {/* Improved popular badge */}
      {isPopular && (
        <div className="absolute top-0 inset-x-0 z-10 flex justify-center translate-y-0">
          <div className="bg-white dark:bg-zinc-900 text-[#4d2ef5] font-bold py-1.5 px-4 rounded-b-xl shadow-lg text-xs tracking-wide">
            MOST POPULAR
          </div>
        </div>
      )}

      <div className="p-8 relative z-1">
        <h3
          className={`text-2xl font-bold mb-2 ${
            isPopular ? 'text-white' : 'text-zinc-900 dark:text-white'
          }`}
        >
          {title}
        </h3>

        {description && (
          <p
            className={`mb-6 ${
              isPopular ? 'text-zinc-100' : 'text-zinc-600 dark:text-zinc-400'
            }`}
          >
            {description}
          </p>
        )}

        <div className="mb-8">
          <div className="flex items-baseline">
            <span
              className={`text-6xl font-bold ${
                isPopular ? 'text-white' : 'text-zinc-900 dark:text-white'
              }`}
            >
              $
              <AnimatePresence mode="wait">
                <motion.span
                  key={isYearly ? 'yearly' : 'monthly'}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{
                    duration: 0.3,
                    ease: [0, 0.7, 0.5, 1],
                  }}
                  className="inline-block"
                >
                  {isYearly ? (
                    <Counter from={monthlyPrice} to={yearlyPricePerMonth} />
                  ) : (
                    <Counter from={yearlyPricePerMonth} to={monthlyPrice} />
                  )}
                </motion.span>
              </AnimatePresence>
            </span>
            <span
              className={`text-xl ml-1 ${
                isPopular ? 'text-zinc-200' : 'text-zinc-500 dark:text-zinc-400'
              }`}
            >
              /month
            </span>
          </div>
          <div
            className={`text-sm mt-2 ${
              isPopular ? 'text-zinc-200' : 'text-zinc-500 dark:text-zinc-400'
            }`}
          >
            {isYearly
              ? `Total billed annually (${yearlyTotal.toFixed(2)}/year)`
              : 'Billed monthly.'}
          </div>
        </div>

        {isYearly && (
          <div className="mt-1 mb-4">
            <span
              className={cn(
                'inline-flex items-center rounded-full px-4 py-2 text-base font-extrabold shadow-lg transform -rotate-2',
                isPopular
                  ? 'bg-white text-[#4d2ef5] animate-pulse'
                  : 'bg-gradient-to-r from-[#4d2ef5] to-[#7d51ed] text-white animate-pulse',
              )}
            >
              <svg
                viewBox="0 0 24 24"
                className="size-5 mr-1.5 -ml-1"
                fill="currentColor"
              >
                <path d="M12.79 21L3 11.21v2c0 .53.21 1.04.59 1.41l7.79 7.79c.78.78 2.05.78 2.83 0l6.21-6.21c.78-.78.78-2.05 0-2.83L12.79 21z" />
                <path d="M11.38 17.41c.39.39.9.59 1.41.59.51 0 1.02-.2 1.41-.59l6.21-6.21c.78-.78.78-2.05 0-2.83l-7.79-7.79C12.25 0.21 11.74 0 11.21 0H5C3.9 0 3 .9 3 2v6.21c0 .53.21 1.04.59 1.41l7.79 7.79zM5 2h6.21L19 9.79 12.79 16 5 8.21V2z" />
                <circle cx="7.25" cy="4.25" r="1.25" />
              </svg>
              SAVE 20%
            </span>
          </div>
        )}

        {isPopular ? (
          <motion.div
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.98 }}
            className="relative"
          >
            <div className="absolute -inset-0.5 bg-gradient-to-r from-[#ffffff] via-[#a594fa] to-[#ffffff] rounded-xl opacity-50 blur-sm group-hover:opacity-75 transition duration-1000 group-hover:duration-200 animate-pulse" />
            <Button
              onClick={() => {
                router.push('/settings?state=subscription');
              }}
              className="w-full h-12 rounded-xl bg-white text-[#4d2ef5] hover:bg-zinc-100 font-medium relative z-10"
            >
              Get Started
            </Button>
          </motion.div>
        ) : (
          <Button
            onClick={() => {
              router.push('/settings?state=subscription');
            }}
            className="w-full h-12 rounded-xl bg-zinc-900 hover:bg-zinc-800 text-white dark:bg-[#4d2ef5] dark:hover:bg-[#3f25cc] dark:text-white shadow-lg shadow-[#4d2ef5]/20 font-medium"
          >
            Get Started
          </Button>
        )}

        <div className="mt-8">
          <h4
            className={`font-medium mb-4 ${
              isPopular ? 'text-white' : 'text-zinc-900 dark:text-white'
            }`}
          >
            Includes
          </h4>
          <ul className="space-y-3">
            {features.map((feature, index) => (
              <motion.li
                key={index}
                className="flex items-start"
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: 0.1 * index }}
              >
                <div
                  className={`shrink-0 size-5 mr-3 mt-0.5 rounded-full flex items-center justify-center ${
                    isPopular
                      ? 'bg-white/20'
                      : 'bg-[#4d2ef5]/10 dark:bg-[#4d2ef5]/20'
                  }`}
                >
                  <Check
                    className={`size-3 ${
                      isPopular
                        ? 'text-white'
                        : 'text-[#4d2ef5] dark:text-[#7d65fa]'
                    }`}
                  />
                </div>
                <span
                  className={
                    isPopular
                      ? 'text-white'
                      : 'text-zinc-600 dark:text-zinc-300'
                  }
                >
                  {feature}
                </span>
              </motion.li>
            ))}
          </ul>
        </div>

        {/* Decorative elements for popular card */}
        {isPopular && (
          <>
            <div className="absolute top-1/4 right-4 size-20 bg-white/5 rounded-full blur-xl" />
            <div className="absolute bottom-1/4 left-4 size-16 bg-white/5 rounded-full blur-lg" />
          </>
        )}
      </div>
    </motion.div>
  );
}
